import React, { useCallback, useState } from 'react';
import PropTypes, { InferProps } from 'prop-types';
import { useMutation } from '@apollo/client';
import ValidationError from '@fuww/library/src/ValidationError';
import registerWithTokenMutation
  from '../../lib/mutations/registerWithToken.graphql';
import loginWithTokenMutation
  from '../../lib/mutations/loginWithToken.graphql';
import SocialLoginButton from './SocialLoginButton';
import { Router } from '../../routes.mjs';
import { Registrar } from '../../lib/graphql/api/graphql';

const signupWithProviderFormPropertyTypes = {
  provider: PropTypes.oneOf(['google', 'linkedin'] as const).isRequired,
  buyer: PropTypes.bool,
};

type SignupWithProviderFormProperties =
  InferProps<typeof signupWithProviderFormPropertyTypes>;

const SignupWithProviderForm = ({
  provider, buyer = false,
}: SignupWithProviderFormProperties) => {
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [disabled, setDisabled] = useState(false);

  const onError = (error: Error) => {
    setDisabled(false);
    setErrorMessage(error.message);
  };

  const [
    createSession, { client },
  ] = useMutation(loginWithTokenMutation, {
    onCompleted: (data) => {
      setDisabled(false);
      localStorage.setItem('accessToken', data.loginWithToken.accessToken);
      client.resetStore();
      Router.pushRoute(
        'edit-user-profile',
        buyer ? { market: true } : {},
      );
    },
    onError,
  });

  const [
    registerUserWithToken,
  ] = useMutation(registerWithTokenMutation, {
    onError,
  });

  return (
    <>
      <SocialLoginButton
        labelKey="register.signUpWithProvider"
        provider={provider}
        onLogin={useCallback(() => setDisabled(true), [setDisabled])}
        onLoginSuccess={useCallback(async ({
          accessToken,
          providerArgument,
          registeredVia,
        }: {
          accessToken?: string;
          providerArgument: string;
          registeredVia: Registrar;
        }) => {
          const result = await registerUserWithToken({
            variables: {
              accessToken,
              provider: providerArgument,
              registeredVia,
            },
          });
          if (result.data) {
            await createSession({
              variables: {
                accessToken,
                provider: providerArgument,
              },
            });
          }
        }, [registerUserWithToken, createSession])}
        setErrorMessage={setErrorMessage}
        setDisabled={setDisabled}
        disabled={disabled}
      />
      { errorMessage ? (
        <ValidationError>
          { errorMessage }
        </ValidationError>
      ) : null}
    </>
  );
};

SignupWithProviderForm.propTypes = signupWithProviderFormPropertyTypes;

export default SignupWithProviderForm;
